import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { baseOrganizationPayloadOf, setupStepView, type SetupView } from '../../lib/types/setup.js';
import { formatRialFa } from '../../lib/finance-display.js';
import { JOURNAL_STATUS_LABEL } from '../../lib/types/finance.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type { AccountListResponse, JournalEntry } from '../../lib/types/finance.js';
import { isReady, PrintLoadState, PrintPageShell, TenantIdentityBlock } from './PrintChrome.js';

const cell = { padding: '6px 8px', border: '1px solid #d1d5db' } as const;
const numCell = { ...cell, textAlign: 'left' as const, fontFamily: 'monospace' };
const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse' as const,
  fontSize: 12,
  marginTop: 12,
  marginBottom: 16,
};

export function JournalVoucherPrintPage(): ReactNode {
  const { unit, label } = useAmountDisplay();
  const { slug, journalId } = useParams<{ slug: string; journalId: string }>();
  const base = slug ? tenantBasePath(slug) : '';

  const journal = useApiGet<JournalEntry>(
    slug && journalId ? `${base}/api/v1/accounting/journal-entries/${journalId}` : null,
    [slug, journalId],
  );

  const accounts = useApiGet<AccountListResponse>(slug ? `${base}/api/v1/finance/accounts` : null, [
    slug,
  ]);

  const setup = useApiGet<SetupView>(slug ? `${base}/api/v1/setup` : null, [slug]);

  if (!slug || !journalId) return null;

  if (!isReady(journal) || !isReady(setup)) {
    return (
      <PrintPageShell backHref={`${base}/journals/${journalId}`} backLabel="بازگشت به سند">
        <PrintLoadState error={journal.status === 'error' ? journal.error : undefined} />
      </PrintPageShell>
    );
  }

  const j = journal.data;
  const accountsMap = new Map<string, { code: string; name: string }>();
  if (isReady(accounts)) {
    for (const a of accounts.data.items) {
      accountsMap.set(a.accountId, { code: a.code, name: a.name });
    }
  }

  const baseOrg = isReady(setup)
    ? baseOrganizationPayloadOf(setupStepView(setup.data, 'base-organization'))
    : null;

  const voucherNumber = j.officialDocumentNumber ?? j.provisionalNumber;
  const statusLabel = (JOURNAL_STATUS_LABEL as Record<string, string>)[j.status] ?? j.status;
  const lines = j.lines ?? [];

  return (
    <PrintPageShell backHref={`${base}/journals/${journalId}`} backLabel="بازگشت به سند">
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          borderBottom: '2px solid #111',
          paddingBottom: 14,
        }}
      >
        <TenantIdentityBlock
          legalIdentity={baseOrg?.legalIdentity ?? null}
          primaryLocation={baseOrg?.primaryLocation ?? null}
          personType={baseOrg?.personType ?? null}
        />
        <div style={{ textAlign: 'left', minWidth: 200 }}>
          <h1 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 6px 0', textAlign: 'center' }}>
            سند حسابداری
          </h1>
          <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div>
              <span style={{ color: '#555' }}>شماره سند: </span>
              <span style={{ fontWeight: 700, fontFamily: 'monospace' }}>{voucherNumber}</span>
            </div>
            <div>
              <span style={{ color: '#555' }}>تاریخ سند: </span>
              <span style={{ fontWeight: 600 }}>{displayJalaliDate(j.businessDate)}</span>
            </div>
            <div>
              <span style={{ color: '#555' }}>وضعیت سند: </span>
              <span style={{ fontWeight: 600 }}>{statusLabel}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Meta Bar */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 12,
          padding: '10px 12px',
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          marginTop: 12,
          fontSize: 12,
        }}
      >
        <div>
          <span style={{ color: '#6b7280' }}>نوع رویداد: </span>
          <span style={{ fontWeight: 600 }}>{j.journalType ?? 'عمومی (GL)'}</span>
        </div>
        <div>
          <span style={{ color: '#6b7280' }}>ماژول صادرکننده: </span>
          <span style={{ fontWeight: 600 }}>{j.sourceModule}</span>
        </div>
        <div>
          <span style={{ color: '#6b7280' }}>مرجع پیگیری: </span>
          <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>
            {j.sourceReference ?? '—'}
          </span>
        </div>
        {j.description && (
          <div style={{ gridColumn: 'span 3' }}>
            <span style={{ color: '#6b7280' }}>شرح سند: </span>
            <span style={{ fontWeight: 600 }}>{j.description}</span>
          </div>
        )}
      </div>

      {/* Journal Lines Table */}
      <table style={tableStyle}>
        <thead>
          <tr style={{ backgroundColor: '#f3f4f6', fontWeight: 700 }}>
            <th style={{ ...cell, width: 45, textAlign: 'center' }}>ردیف</th>
            <th style={{ ...cell, width: 110, textAlign: 'center' }}>کد حساب</th>
            <th style={{ ...cell, width: 180, textAlign: 'right' }}>نام حساب</th>
            <th style={{ ...cell, textAlign: 'right' }}>شرح آرتیکل</th>
            <th style={{ ...cell, width: 130, textAlign: 'left' }}>بدهکار ({label})</th>
            <th style={{ ...cell, width: 130, textAlign: 'left' }}>بستانکار ({label})</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => {
            const acc = accountsMap.get(l.accountId);
            return (
              <tr key={l.rowNumber}>
                <td style={{ ...cell, textAlign: 'center' }}>{l.rowNumber}</td>
                <td style={{ ...cell, textAlign: 'center', fontFamily: 'monospace' }}>
                  {acc?.code ?? l.accountId.slice(0, 8)}
                </td>
                <td style={{ ...cell, fontWeight: 600 }}>{acc?.name ?? '—'}</td>
                <td style={{ ...cell, color: '#374151' }}>{l.description ?? '—'}</td>
                <td style={numCell}>
                  {BigInt(l.debitRial) > 0n ? formatRialFa(l.debitRial, unit) : '—'}
                </td>
                <td style={numCell}>
                  {BigInt(l.creditRial) > 0n ? formatRialFa(l.creditRial, unit) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ backgroundColor: '#f9fafb', fontWeight: 800, fontSize: 13 }}>
            <td colSpan={4} style={{ ...cell, textAlign: 'right' }}>
              جمع کل
            </td>
            <td style={numCell}>{formatRialFa(j.debitTotal, unit)}</td>
            <td style={numCell}>{formatRialFa(j.creditTotal, unit)}</td>
          </tr>
        </tfoot>
      </table>

      {/* Balance Verification Note */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 11.5,
          color: '#4b5563',
          padding: '0 4px',
        }}
      >
        <span>
          {BigInt(j.debitTotal) === BigInt(j.creditTotal)
            ? '✓ سند حسابداری کاملاً تراز و منطبق است.'
            : '⚠ هشدار: سند دارای مغایرت بدهکار و بستانکار است.'}
        </span>
        <span style={{ fontSize: 10, color: '#9ca3af' }}>نسخه چاپی سیستم مالی CloudERP</span>
      </div>

      {/* Official Signatures Grid (Maker-Checker & Signoffs) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 16,
          marginTop: 48,
          borderTop: '1px dashed #d1d5db',
          paddingTop: 16,
          textAlign: 'center',
          fontSize: 12,
        }}
      >
        <div>
          <div style={{ fontWeight: 700, marginBottom: 40 }}>تنظیم‌کننده (Maker)</div>
          <div style={{ borderBottom: '1px solid #9ca3af', width: '80%', margin: '0 auto' }} />
        </div>
        <div>
          <div style={{ fontWeight: 700, marginBottom: 40 }}>رسیدگی‌کننده (Checker)</div>
          <div style={{ borderBottom: '1px solid #9ca3af', width: '80%', margin: '0 auto' }} />
        </div>
        <div>
          <div style={{ fontWeight: 700, marginBottom: 40 }}>مدیر مالی</div>
          <div style={{ borderBottom: '1px solid #9ca3af', width: '80%', margin: '0 auto' }} />
        </div>
        <div>
          <div style={{ fontWeight: 700, marginBottom: 40 }}>مدیرعامل / مقام مجاز</div>
          <div style={{ borderBottom: '1px solid #9ca3af', width: '80%', margin: '0 auto' }} />
        </div>
      </div>
    </PrintPageShell>
  );
}
